import Link from "../../components/router-link";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { ServiceExplorer } from "../../components/service-explorer";
import { CTA, PageHero, SectionHeading } from "../../components/site-shell";
import { createMetadata } from "../../lib/seo";
import { serviceLandingPages } from "../../lib/landing-pages";

export const metadata = createMetadata({
  title: "品牌增长、官网与SEO/GEO服务",
  description:
    "提供品牌定位、企业官网与海外独立站建设、内容营销、SEO/GEO 优化、市场增长及线索运营服务，覆盖从品牌认知到客户咨询的完整链路。",
  path: "/services",
  keywords: [
    "企业品牌增长服务",
    "品牌定位策划",
    "产品价值表达",
    "企业官网建设",
    "海外独立站建设",
    "外贸独立站建设",
    "SEO优化服务",
    "技术SEO服务",
    "GEO优化服务",
    "生成式引擎优化",
    "AI搜索优化服务",
    "内容营销服务",
    "海外营销",
    "线索运营",
    "市场增长服务",
  ],
  image: "/assets/seo与geo持续优化.png",
});

const flow = [
  {
    title: "业务沟通",
    phase: "初步合作方向",
    responsibility: "了解业务、产品、目标客户与当前增长问题。",
    cooperation: "说明现状与预期，明确预算范围和参与决策人。",
  },
  {
    title: "增长诊断",
    phase: "诊断结论与优先级",
    responsibility: "评估品牌表达、内容、网站、渠道与获客基础。",
    cooperation: "提供现有资料、历史数据与真实业务情况。",
  },
  {
    title: "方案与合作确认",
    phase: "合作方案与项目计划",
    responsibility: "明确目标、范围、周期、关键动作、交付物与报价。",
    cooperation: "确认范围、项目负责人、合同及启动时间。",
  },
  {
    title: "项目启动与建设改造",
    phase: "增长基础具备运营条件",
    responsibility:
      "组建项目团队，完成策略、内容、网站的建设或改造，并配置数据与获客基础。",
    cooperation: "指定对接人、提交资料，并在关键节点集中确认。",
  },
  {
    title: "运营启动与验证",
    phase: "运营机制开始运转",
    responsibility: "上线首批内容和获客动作，验证数据追踪与线索承接。",
    cooperation: "及时跟进线索，反馈客户质量与一线销售情况。",
  },
  {
    title: "周期运营与持续迭代",
    phase: "周期报告与下阶段计划",
    responsibility:
      "按月度或季度持续执行内容、曝光与获客，并依据数据复盘优化。",
    cooperation: "反馈线索、销售、产品与市场变化，共同确认下一周期重点。",
  },
];

export default function ServicesPage() {
  return (
    <>
      <PageHero
        light
        eyebrow="Growth services"
        title="企业品牌增长、官网建设与 SEO/GEO 服务"
        description="以企业业务目标为起点，提供品牌、网站、内容、搜索、线索与运营一体化的增长服务。"
        image="/assets/seo与geo持续优化.png"
      />
      <section className="section-shell section-block">
        <SectionHeading
          eyebrow="Core services"
          title="六项核心能力，覆盖从品牌认知到客户咨询的关键链路。"
          description="每项服务都围绕企业可长期掌握的品牌资产、内容资产、搜索曝光和客户渠道展开。"
        />
        <ServiceExplorer />
        <nav
          className="seo-related-links seo-index-links"
          aria-label="服务详情"
        >
          {serviceLandingPages.map((page) => (
            <Link href={page.path} key={page.path}>
              <span>{page.pageTitle}</span>
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          ))}
        </nav>
      </section>
      <section id="combination" className="section-muted section-block">
        <div className="section-shell">
          <SectionHeading
            eyebrow="Collaboration process"
            title="合作流程与持续运营机制。"
            description="先把增长基础建好，再按月度或季度持续运营。我们不是“做完即交付”，而是从诊断、建设到数据复盘，持续推进品牌、曝光与获客。"
          />
          <div className="collaboration-process">
            <div className="collaboration-process-head" aria-hidden="true">
              <span>合作阶段</span>
              <span>我们负责</span>
              <span>企业需要配合</span>
            </div>
            <ol className="collaboration-process-list">
              {flow.map(
                ({ title, phase, responsibility, cooperation }, index) => (
                  <li key={title}>
                    <div className="collaboration-stage">
                      <strong>0{index + 1}</strong>
                      <div>
                        <h3>{title}</h3>
                        <p>{phase}</p>
                      </div>
                    </div>
                    <p>{responsibility}</p>
                    <p>{cooperation}</p>
                  </li>
                ),
              )}
            </ol>
          </div>
        </div>
      </section>
      <CTA
        title="从明确当前问题开始，建立下一阶段增长路径。"
        description="与增长顾问沟通产品、市场与现有基础，获得适合当前阶段的优先事项建议。"
      />
    </>
  );
}
